import React from 'react';

/**
 * Session Timeout Warning Modal
 *
 * Displays a warning modal when user inactivity reaches the warning threshold.
 * Shows a live countdown timer and provides options to stay logged in or logout immediately.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {number} props.remainingSeconds - Remaining seconds before auto-logout
 * @param {Function} props.onStayLoggedIn - Callback when user clicks "Stay Logged In"
 * @param {Function} props.onLogoutNow - Callback when user clicks "Log Out Now"
 */
const SessionTimeoutModal = ({
  isOpen,
  remainingSeconds,
  onStayLoggedIn,
  onLogoutNow
}) => {
  if (!isOpen) return null;

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} second${secs !== 1 ? 's' : ''}`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        animation: 'fadeIn 0.2s ease-in'
      }}
      onClick={(e) => {
        // Close modal if user clicks outside
        if (e.target === e.currentTarget) {
          onStayLoggedIn();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '480px',
          width: '90%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          animation: 'slideIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 20px',
          backgroundColor: '#fff3cd',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '4px solid #ffc107'
        }}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#856404"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 16px',
          textAlign: 'center',
          fontSize: '24px',
          fontWeight: '700',
          color: '#333'
        }}>
          You will be logged out soon
        </h2>

        {/* Message */}
        <p style={{
          margin: '0 0 24px',
          textAlign: 'center',
          fontSize: '16px',
          color: '#666',
          lineHeight: '1.5'
        }}>
          You will be logged out in{' '}
          <strong style={{ color: '#dc3545', fontSize: '18px' }}>
            {formatTime(remainingSeconds)}
          </strong>
          {' '}due to inactivity.
        </p>

        {/* Countdown Display */}
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          textAlign: 'center',
          border: '2px solid #dee2e6'
        }}>
          <div style={{
            fontSize: '48px',
            fontWeight: '700',
            color: remainingSeconds <= 60 ? '#dc3545' : '#ffc107',
            fontFamily: 'monospace',
            letterSpacing: '2px'
          }}>
            {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6c757d',
            marginTop: '4px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Time Remaining
          </div>
        </div>

        {/* Info Message */}
        <p style={{
          margin: '0 0 24px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          Click anywhere or interact with the page to stay logged in
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          <button
            onClick={onLogoutNow}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              ':hover': {
                backgroundColor: '#5a6268'
              }
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
          >
            Log Out Now
          </button>
          <button
            onClick={onStayLoggedIn}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#218838';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#28a745';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.3)';
            }}
          >
            Stay Logged In
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SessionTimeoutModal;
